import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, styled } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { normalizeFileName } from "../../../../../../common/utils/utils";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { GEDDocumento } from "../../../../../../common/domain/models/ged_documento";
import { zodResolver } from "@hookform/resolvers/zod";
import { GedDocumentoForm, GedDocumentoSchema } from "../../../schemas/GED_internos/ged_documento";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { DefaultFormRadioGroupInput } from "../../../../../../common/components/default_form_radio_group_input";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * CopyDocumentoModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  selectedDocumento: GEDDocumento | null;
  centroCustoDropdown: CentroCustoDropDown[];
  funcionariosDropdown: FuncionarioDropDown[];
  onClose: () => void;
  onSubmit: (form: GedDocumentoForm) => void;
  onDownloadFile: () => void;
  onDownloadFilePdf: () => void;
};

export const CopyDocumentoModal = ({
  open,
  saving,
  selectedDocumento,
  centroCustoDropdown,
  funcionariosDropdown,
  onClose,
  onSubmit,
  onDownloadFile,
  onDownloadFilePdf,
}: Props) => {
  const hookForm = useForm<GedDocumentoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(GedDocumentoSchema),
  });

  const [gedDocumento, setGedDocumento] = useState<GEDDocumento | null>(null);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Obsoleto',
        style: {
          backgroundColor: '#f3565d',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 2,
        label: 'Atual',
        style: {
          backgroundColor: '#45b6af',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  const tipoOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Documento',
        style: {
          backgroundColor: '#f8cb00',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 2,
        label: 'Registro',
        style: {
          backgroundColor: '#89c4f4',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  useEffect(() => {
    if (open) {
      if (selectedDocumento) {
        hookForm.setValue('statusArquivo', selectedDocumento.obsoleto === true ? 1 : 2);
        hookForm.setValue('tipo', selectedDocumento.tipo === 0 ? 1 : 2);
        hookForm.setValue('arquivo', selectedDocumento.descricao ?? '');
        hookForm.setValue('versao', selectedDocumento.versao);
        hookForm.setValue('centroCusto', selectedDocumento.idCentroCusto);
        hookForm.setValue('revisadoPor', selectedDocumento.idFuncionarioRevisado);
        hookForm.setValue('aprovadoPor', selectedDocumento.idFuncionarioAprovado);
        hookForm.setValue('responsavelProcesso', selectedDocumento.idFuncionarioResponsavel);
        hookForm.setValue('localArmazenamento1', selectedDocumento.local1);
        hookForm.setValue('localArmazenamento2', selectedDocumento.local2);
        hookForm.setValue('acesso', selectedDocumento.local3);
        hookForm.setValue('recuperacao', selectedDocumento.local4);
        hookForm.setValue('protecao', selectedDocumento.local5);
        hookForm.setValue('retencao', selectedDocumento.local6);
        hookForm.setValue('disposicao', selectedDocumento.disposicao);
        hookForm.setValue('observacoes', selectedDocumento.observacao);
        hookForm.setValue('file', null);
        hookForm.setValue('filePdf', null);
        hookForm.setValue('limparArquivo', true);
        hookForm.setValue('limparArquivoPdf', true);

        setGedDocumento(({ ...selectedDocumento, nome: null, nomePdf: null }));
      }
    }
  }, [
    open,
    selectedDocumento,
    hookForm,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [
    saving,
    onClose,
  ]);

  const handleSubmit = useCallback((form: any) => {
    if (!saving) onSubmit(form);
  }, [
    saving,
    onSubmit,
  ]);

  const handleDownloadFile = useCallback(() => {
    if (!saving) onDownloadFile();
  }, [
    saving,
    onDownloadFile,
  ]);

  const handleDownloadPdf = useCallback(() => {
    if (!saving) onDownloadFilePdf();
  }, [
    saving,
    onDownloadFilePdf,
  ]);

  const handleDeleteFile = useCallback(() => {
    hookForm.setValue('limparArquivo', true);
    setGedDocumento(doc => {
      if (doc === null) return doc;
      return ({ ...doc, nome: null, arquivo: null });
    });
  }, [
    hookForm,
  ]);

  const handleDeletePdf = useCallback(() => {
    if (window.confirm('Você deseja excluir este item?')) {
      hookForm.setValue('limparArquivoPdf', true);
      setGedDocumento(doc => {
        if (doc === null) return doc;
        return ({ ...doc, nomePdf: null, arquivoPdf: null });
      });
    }
  }, [
    hookForm,
  ]);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
    if (!saving && e.target.files && e.target.files.length > 0) {
      const f = e.target.files.item(0);
      if (f) {
        hookForm.setValue('limparArquivo', false);
        hookForm.setValue('file', f);



        onChange(f);
      }
    }
  }, [
    saving,
    hookForm,
  ]);

  const handlePdfChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
    if (!saving && e.target.files && e.target.files.length > 0) {
      const f = e.target.files.item(0);
      if (f) {
        hookForm.setValue('limparArquivoPdf', false);
        hookForm.setValue('filePdf', f);



        onChange(f);
      }
    }
  }, [
    saving,
    hookForm,
  ]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "65rem",
          },
        },
      }}>
      <DialogTitle>
        Copiar documento
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow" gap="small">
            <>
              <DefaultFormRadioGroupInput
                name='statusArquivo'
                label='Status do Arquivo'
                options={statusOptions}
                reactHookForm={hookForm}
              />

              <DefaultFormRadioGroupInput
                name='tipo'
                label='Tipo'
                options={tipoOptions}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'arquivo'}
                label={"Arquivo"}
                readOnly={saving}
                required={true}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'versao'}
                label={"Versão"}
                readOnly={saving}
                reactHookForm={hookForm}
              />

              <DefaultFormAutocomplete
                name='centroCusto'
                label='Centro de Custo'
                options={centroCustoDropdown}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm}
              />

              <DefaultFormAutocomplete
                name='revisadoPor'
                label='Revisado Por'
                getOptionDisabled={x => !x.canBeSelected}
                options={funcionariosDropdown}
                getOptionKey={x => x.idFuncionario}
                getOptionLabel={x => x.nome}
                reactHookForm={hookForm}
              />

              <DefaultFormAutocomplete
                name='aprovadoPor'
                label='Aprovado Por'
                getOptionDisabled={x => !x.canBeSelected}
                options={funcionariosDropdown}
                getOptionKey={x => x.idFuncionario}
                getOptionLabel={x => x.nome}
                reactHookForm={hookForm}
              />

              <DefaultFormAutocomplete
                name='responsavelProcesso'
                label='Responsável Pelo Processo'
                getOptionDisabled={x => !x.canBeSelected}
                options={funcionariosDropdown}
                getOptionKey={x => x.idFuncionario}
                getOptionLabel={x => x.nome}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name='localArmazenamento1'
                label='Local de Armazenamento 1'
                readOnly={saving}
                multiline={true}
                rows={5}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name='localArmazenamento2'
                label='Local de Armazenamento 2'
                readOnly={saving}
                multiline={true}
                rows={5}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name='acesso'
                label='Acesso'
                readOnly={saving}
                multiline={true}
                rows={5}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name='recuperacao'
                label='Recuperação'
                readOnly={saving}
                multiline={true}
                rows={5}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name='protecao'
                label='Proteção'
                readOnly={saving}
                multiline={true}
                rows={5}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name='retencao'
                label='Retenção'
                readOnly={saving}
                multiline={true}
                rows={5}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name='disposicao'
                label='Disposição'
                readOnly={saving}
                multiline={true}
                rows={5}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name='observacoes'
                label='Observações'
                readOnly={saving}
                multiline={true}
                rows={10}
                reactHookForm={hookForm}
              />

              <div className="flex w-full">
                <Controller
                  name='file'
                  control={hookForm.control}
                  render={({ field }) => (gedDocumento && gedDocumento.nome && gedDocumento.nome.length > 0) ? (
                    <div className="w-full flex flex-row justify-between items-center">
                      <span
                        className="cursor-pointer"
                        onMouseDown={handleDownloadFile}>
                        {gedDocumento && gedDocumento.nome && gedDocumento.nome.length > 40
                          ? `${gedDocumento.nome.substring(0, 40)}...`
                          : gedDocumento.nome}
                      </span>
                      <IconButton onClick={handleDeleteFile}>
                        <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                      </IconButton>
                    </div>
                  ) : (
                    <PrimaryButton
                      component="label"
                      role={undefined}
                      variant="contained"
                      tabIndex={-1}
                      disabled={saving}
                      startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                      {field.value ? normalizeFileName(field.value?.name, 40) : "Escolher arquivo editável"}
                      <VisuallyHiddenInput
                        type="file"
                        onChange={(e) => handleFileChange(e, field.onChange)}
                        multiple={false} />
                    </PrimaryButton>
                  )} />
              </div>

              <div className="flex w-full">
                <Controller
                  name='filePdf'
                  control={hookForm.control}
                  render={({ field }) => (gedDocumento && gedDocumento.nomePdf && gedDocumento.nomePdf.length > 0) ? (
                    <div className="w-full flex flex-row justify-between items-center">
                      <span
                        className="cursor-pointer"
                        onMouseDown={handleDownloadPdf}>
                        {gedDocumento && gedDocumento.nomePdf && gedDocumento.nomePdf.length > 40
                          ? `${gedDocumento.nomePdf.substring(0, 40)}...`
                          : gedDocumento.nomePdf}
                      </span>
                      <IconButton onClick={handleDeletePdf}>
                        <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                      </IconButton>
                    </div>
                  ) : (
                    <PrimaryButton
                      component="label"
                      role={undefined}
                      variant="contained"
                      tabIndex={-1}
                      disabled={saving}
                      startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                      {field.value ? normalizeFileName(field.value?.name, 40) : "Escolher arquivo PDF"}
                      <VisuallyHiddenInput
                        type="file"
                        accept=".pdf"
                        onChange={(e) => handlePdfChange(e, field.onChange)}
                        multiple={false} />
                    </PrimaryButton>
                  )} />
              </div>
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div></div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}